/**
 * Read-only banner write-gate (dizajn B10).
 *
 * Ukáže sa, keď existuje identita (nsec alebo NIP-07), ale gateway povedal
 * `allowed: false` — návštevník môže čítať, len nemôže písať. Banner je
 * dismissible (skryje sa do ďalšej zmeny identity/pubkeyu) a odkazuje na
 * úvodný topic inštancie, kde je návod „ako sa pripojiť“.
 *
 * Dva stavy textu, ktoré sa nesmú zamieňať:
 *  - `denied`      → gateway vedome odpovedal „nie si operátor“, ukážeme
 *    jeho slovenský reason (napr. „pubkey nie je v zozname operátorov“),
 *  - `unavailable` → status sme nezistili (gateway neodpovedal) → neutrálny
 *    text `WRITE_STATUS_UNAVAILABLE`, žiadne tvrdenie o používáčovi.
 *
 * Anonym (žiadna identita) banner nedostáva — composer od neho vyžaduje
 * identitu skôr, než vôbec niečo pošle.
 */
import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router';

import { HOW_IT_WORKS_SLUG, WRITE_STATUS_UNAVAILABLE, useWriteStatus } from '../hooks/useWriteStatus.js';
import { useIdentity } from '../nostr/IdentityContext.js';

/** text banneru (B10) — link je na slová „ako sa pripojiť“ */
export const WRITE_BANNER_TEXT = 'Písanie je len pre operátorov, ktorí zrkadlia fórum —';

export function ReadOnlyBanner(): JSX.Element | null {
  const { identity } = useIdentity();
  const write = useWriteStatus();
  const [dismissed, setDismissed] = useState(false);

  // nová identita / iný signer = nová situácia → banner opäť ukážeme
  useEffect(() => {
    setDismissed(false);
  }, [write.pubkey]);

  if (identity === null) return null;
  if (!write.locked) return null;
  if (dismissed) return null;

  return (
    <div className="write-banner" role="status" data-testid="write-banner">
      <span className="write-banner-text" data-testid="write-banner-text">
        {WRITE_BANNER_TEXT}{' '}
        <Link to={`/topics/${HOW_IT_WORKS_SLUG}`} data-testid="write-banner-link">
          ako sa pripojiť
        </Link>
      </span>
      {write.kind === 'denied' && write.reason !== null && (
        <span className="write-banner-reason" data-testid="write-banner-reason" title={write.pubkey ?? undefined}>
          {write.reason}
        </span>
      )}
      {write.kind === 'unavailable' && (
        <span className="write-banner-note" data-testid="write-banner-note">
          {WRITE_STATUS_UNAVAILABLE}
        </span>
      )}
      <button
        type="button"
        className="write-banner-close"
        aria-label="zavrieť oznámenie"
        data-testid="write-banner-dismiss"
        onClick={() => {
          setDismissed(true);
        }}
      >
        ×
      </button>
    </div>
  );
}
